'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HomeCTABanner() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background w-full px-4 md:px-12 pt-12 md:pt-16 pb-16 md:pb-20">
      <div className="max-w-7xl mx-auto">
        <div className="bg-dark-bg noise-overlay rounded-3xl md:rounded-5xl p-7 md:p-16 text-center relative overflow-hidden reveal">
          {/* Decorative corner motifs — desktop only */}
          <div className="absolute top-4 left-4 w-40 lg:w-56 h-10 lg:h-14 opacity-40 pointer-events-none hidden md:block">
            <AppImage
              src="/assets/images/Motif-03.png"
              alt=""
              fill
              className="object-contain object-left"
              style={{ objectFit: 'contain' }}
              sizes="220px" />
          </div>
          <div className="absolute bottom-4 right-4 w-40 lg:w-56 h-10 lg:h-14 opacity-40 rotate-180 pointer-events-none hidden md:block">
            <AppImage
              src="/assets/images/Motif-03.png"
              alt=""
              fill
              className="object-contain object-left"
              style={{ objectFit: 'contain' }}
              sizes="220px" />
          </div>
          <div className="absolute top-4 right-4 w-20 lg:w-28 h-20 lg:h-28 opacity-20 pointer-events-none hidden md:block">
            <AppImage
              src="/assets/images/Motif-01.png"
              alt=""
              fill
              className="object-contain object-right-top scale-x-[-1]"
              style={{ objectFit: 'contain' }}
              sizes="140px" />
          </div>
          <div className="absolute bottom-4 left-4 w-20 lg:w-28 h-20 lg:h-28 opacity-20 rotate-180 pointer-events-none hidden md:block">
            <AppImage
              src="/assets/images/Motif-01.png"
              alt=""
              fill
              className="object-contain object-right-top scale-x-[-1]"
              style={{ objectFit: 'contain' }}
              sizes="140px" />
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <span className="inline-block px-4 md:px-5 py-1.5 md:py-2 rounded-full border border-accent/30 text-accent text-xs font-bold tracking-widest uppercase mb-4 md:mb-6">
              Memberships Now Open
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-6xl font-black text-white tracking-tight leading-tight mb-4 md:mb-5">
              Join the <span className="text-accent">HCAF</span> Family
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-6 md:mb-8 max-w-xl mx-auto">
              1000+ members. Four tiers. Unforgettable Durga Pujas.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 md:gap-4">
              <Link
                href="/membership"
                className="inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground pl-7 md:pl-8 pr-3 py-3.5 md:py-4 rounded-full font-bold tracking-wide hover:bg-secondary transition-all duration-300 shadow-crimson text-sm md:text-base group"
              >
                Become a Member
                <span className="bg-primary-foreground/15 p-2 rounded-full group-hover:translate-x-0.5 transition-transform">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                </span>
              </Link>
              <Link
                href="/sponsorship"
                className="inline-flex items-center justify-center gap-3 glass-light text-white pl-7 md:pl-8 pr-3 py-3.5 md:py-4 rounded-full font-bold tracking-wide hover:bg-white/20 transition-all duration-300 text-sm md:text-base group"
              >
                View Sponsorship Packages
                <span className="bg-white/10 p-2 rounded-full group-hover:translate-x-0.5 transition-transform">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                </span>
              </Link>
            </div>
            <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-6 text-white/40 text-xs font-medium tracking-wide">
              <span>🪔 October 16–21, 2026</span>
              <span className="hidden sm:block">·</span>
              <span>🎵 Javed Ali · Shanmukhananda Chandrasekhara Saraswati Auditorium</span>
              <span className="hidden sm:block">·</span>
              <span>📍 South-Central Mumbai</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}